import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import api from '@/lib/api';
import { isSmsChimeAudibleNow, playSmsChime } from '@/lib/smsChime';

const WebSocketContext = createContext(null);

const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, 'ws') || '';

export function WebSocketProvider({ children }) {
  const { user, getWsTicket } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef(null);
  const reconnectAttempt = useRef(0);
  const listenersRef = useRef({});

  const addListener = useCallback((event, fn) => {
    if (!listenersRef.current[event]) listenersRef.current[event] = [];
    listenersRef.current[event].push(fn);
    return () => {
      listenersRef.current[event] = (listenersRef.current[event] || []).filter(f => f !== fn);
    };
  }, []);

  const connect = useCallback(async () => {
    if (!user) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    // Get a short-lived WS ticket via cookie-authenticated endpoint
    const ticket = await getWsTicket();
    if (!ticket) return;

    const wsUrl = `${WS_BASE}/api/ws?token=${ticket}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttempt.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg === 'pong') return;

        const eventType = msg.event;
        const data = msg.data || {};

        // Fire registered listeners
        (listenersRef.current[eventType] || []).forEach(fn => fn(data));
        // Also fire 'any' listeners
        (listenersRef.current['any'] || []).forEach(fn => fn(eventType, data));

        // Show toast for key events
        if (eventType === 'lead_shared') {
          toast.info(`${data.shared_by} shared "${data.lead_name}" with you`, { duration: 5000 });
        } else if (eventType === 'new_message') {
          toast.info(`New message from ${data.sender_name}`, { duration: 4000 });
        } else if (eventType === 'sms_inbound') {
          const leadName = data.lead_name || data.from || 'a lead';
          const preview = data.preview ? ` — ${data.preview}` : '';
          toast.info(`New SMS from ${leadName}${preview}`, { duration: 5000 });
          if (isSmsChimeAudibleNow()) {
            playSmsChime();
          }
        } else if (eventType === 'marketing_video_ready') {
          toast.success(`Marketing video ready: ${data.title || 'your clip'}`, {
            duration: 8000,
            action: data.job_id ? {
              label: 'View',
              onClick: () => { window.location.href = `/admin/marketing-videos?job=${data.job_id}`; },
            } : undefined,
          });
        } else if (eventType === 'lead_assigned') {
          toast.info(`Lead "${data.lead_name}" was auto-assigned to you`, { duration: 5000 });
        } else if (eventType === 'notification') {
          if (data.type === 'warning' && data.link && data.message?.includes('Lost')) {
            const leadId = data.link.split('/leads/')[1];
            toast.warning(data.message, {
              duration: 15000,
              action: leadId ? {
                label: 'Mark as Lost',
                onClick: () => {
                  api.updateLead(leadId, { status: 'lost' }).then(() => toast.success('Lead marked as Lost')).catch(() => toast.error('Failed to update'));
                },
              } : undefined,
            });
          } else {
            toast.info(data.message || data.title, { duration: 4000 });
          }
        }
      } catch (err) { logger.error('WS ticket parse error:', err); }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Auto-reconnect with exponential backoff
      if (user) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 30000);
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [user, getWsTicket]);

  useEffect(() => {
    if (user) {
      connect();
    }
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user, connect]);

  // Ping to keep alive every 25s
  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);
    return () => clearInterval(interval);
  }, [connected]);

  const wsValue = useMemo(() => ({ connected, addListener }), [connected, addListener]);

  return (
    <WebSocketContext.Provider value={wsValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
